// infrastructure/api/billingItemApi.js
import BillingItemsDB from 'src/fb/billingItems-db';

import { toBillingItem } from '../mappers/billingItemMapper';

/**
 * Every billing item a clinic has.
 *
 * THE CLINIC CONSTRAINT IS THE SCOPE, not a convenience: `billingItems` is a flat root collection
 * and the rules require the caller's clinic to match the document's, so a read without it returns
 * nothing at all rather than reaching too far.
 *
 * NO DATE WINDOW, AND THE CLINIC'S OWN HISTORY IS THE BOUND. The period a biller works in is a
 * filter on the table like every other, which means it narrows in memory and changing it must not
 * re-read — so this loads the set those filters run over. "Everything" is smaller than it sounds:
 * the projector never produces an item dated before the clinic's revenue-cycle start date, so a
 * clinic's whole history is exactly what it has billed for, and that is the set a biller searches.
 *
 * THAT IS A BET ON VOLUME, and the one to revisit first. A clinic billing daily for years will
 * eventually make this read too large, and the fix is to page it — the composite index this query
 * already uses, clinic plus service date, is what a pager would need.
 *
 * ORDERED BY SERVICE DATE, ascending, which is the index's order rather than the one a biller
 * reads: every row is in memory and the table sorts them itself, newest first. This order is
 * what a pager would resume from.
 *
 * @param {{clinicId: string}} query
 * @returns {Promise<import('../../domain/model/BillingItem.types').BillingItem[]>}
 */
// eslint-disable-next-line import/prefer-default-export
export async function loadBillingItems({ clinicId }) {
  if (!clinicId) {
    return [];
  }

  const documents = await new BillingItemsDB().readAll(
    [['clinicId', '==', clinicId]],
    'serviceDate',
  );

  return documents.map(toBillingItem);
} // loadBillingItems()
